import { Component, Injector, OnDestroy, OnInit, Type } from '@angular/core';
import { PromotionTypeComponent } from './child-components/promotion-type/promotion-type.component';
import { BasicSettingComponent } from './child-components/basic-setting/basic-setting.component';
import { EligibilitySettingComponent } from './child-components/eligibility-setting/eligibility-setting.component';
import { KycSettingComponent } from './child-components/kyc-setting/kyc-setting.component';
import { MessageSettingComponent } from './child-components/message-setting/message-setting.component';
import { PromotionNameSettingComponent } from './child-components/promotion-name-setting/promotion-name-setting.component';
import { PromotionCurrencySettingComponent } from './child-components/promotion-currency-setting/promotion-currency-setting.component';
import { ProviderBlacklistSettingComponent } from './child-components/provider-blacklist-setting/provider-blacklist-setting.component';
import { EventEmitterService } from '@core/services/event-emitter.service';
import { AppPermissionService } from '@core/services/app-permission.service';
import { Observable, Subscription, forkJoin } from 'rxjs';
import * as moment from 'moment-timezone';
import { PromotionCodeDataService } from '../promotion-codes/services/promotion-code-data.service';
import { catchError, tap, take } from 'rxjs/operators';
import { ActivatedRoute, Router } from '@angular/router';
import { MemberSettingComponent } from './child-components/member-setting/member-setting.component';
import { create } from 'lodash';

interface Step {
  label: string;
  component: Type<any>;
}

@Component({
  selector: 'kt-promotion',
  templateUrl: './promotion-code-2.component.html',
  styleUrls: ['./promotion-code-2.component.scss']
})
export class PromotionCodeComponent2 implements OnInit, OnDestroy {
  currentStepIndex = 0;
  selectedPromotionType: string | null = null;

  formData: any = {
    code: null,
    name: null,
    promotion_ids: [],
    valid_from: null,
    valid_to: null,
    status: 1,
    promotion_names: [],
    promotion_currency: []
  };

  formDataValidated = [
    'code',
    'name',
    'valid_from',
    'promotion_currency'
  ];

  timezone = JSON.parse(localStorage.getItem('user_data')).timezone;
  clientOffset = moment().utcOffset() * 60 * 1000;
  offset = moment.tz(this.timezone).utcOffset() * 60 * 1000;
  dateTimeFormat = 'yyyy-MM-dd HH:mm:';

  stepFormData: { [key: string]: any } = {};

  steps: Step[] = [
    { label: 'Promotion Type', component: PromotionTypeComponent },
  ];
  stepInjectors: Injector[] = [];
  stepDotClasses: { [step: string]: string } = {};
  messages$ = this.promotionCodeDataService.messages$;
  data: any;
  isEditMode: boolean = false;
  canEditPromotionCode: boolean = false; // edit mode shows Update only with edit_promotion_code — mirrors v1's Save
  loading: boolean = false;
  submitting: boolean = false; // true while a create/update request is in flight — disables Submit to block double-clicks
  promotionOption: string = 'free-spin';

  private subscription = new Subscription();
  private stepValidity: { [step: string]: boolean | null } = {};
  private basicSettingSubscription?: Subscription;
  private eligibilitySettingSubscription?: Subscription;
  private memberSettingSubscription?: Subscription;
  private kycSettingSubscription?: Subscription;
  private messageSettingSubscription?: Subscription;
  private promotionNameSettingSubscription?: Subscription;
  private promotionCurrencySettingSubscription?: Subscription;
  private providerBlacklistSubscription?: Subscription;

  constructor(
    private injector: Injector,
    private eventEmitterService: EventEmitterService,
    private promotionCodeDataService: PromotionCodeDataService,
    private route: ActivatedRoute,
    private router: Router,
    private appPermissionService: AppPermissionService
  ) {}

  ngOnInit(){
    // Permission gate — mirror v1: create needs create_promotion_code, edit needs view_promotion_code.
    // The route guard only checks view_promotion_codes_list, so gate per-mode here so a view-only user
    // can't create/edit a promotion via a direct URL to /general/promotion-setup.
    const isEditByParam = !!this.route.snapshot.queryParams['id'];
    this.appPermissionService.getAppPermissions().pipe(take(1)).subscribe(perms => {
      this.canEditPromotionCode = !!perms.edit_promotion_code;
      if (!(isEditByParam ? perms.view_promotion_code : perms.create_promotion_code)) {
        this.router.navigate(['general/promotion-codes']);
      }
    });

    this.route.queryParams.subscribe(params => {
      const promoId = params['id'];
      const promoType = params['type'];

      if (!promoId) {
        this.loading = false;
        this.isEditMode = false;
        this.promotionOption = 'free-spin';
        // Free Spin has no Promotion Type step, so select the type here to build the steps.
        this.onPromotionTypeSelected('Free Spin');
        this.onUpdateStep();
        return;
      }

      // The route guard only checks view_promotion_codes_list, and an edit URL with a missing/unknown
      // type has no loader below — send it back to 3.2.1.
      if (!['btg', 'pp', 'free-spin'].includes(promoType)) {
        this.router.navigate(['general/promotion-codes']);
        return;
      }

      this.loading = true;
      this.isEditMode = true;
      // Edit mode: the tab (Free Spin vs Provider Promotion) follows the promotion being edited,
      // so BTG/PP render the Provider Promotion picker instead of an empty Free Spin panel.
      this.promotionOption = (promoType === 'btg' || promoType === 'pp') ? 'provider-promotion' : 'free-spin';

      // Select correct API method and regex pattern
      let getByIdFn: ((id: any) => Observable<any>) | null = null;
      let codeSuffixPattern: RegExp | null = null;

      if (promoType === 'btg') {
        getByIdFn = this.promotionCodeDataService.getById2.bind(this.promotionCodeDataService);
        codeSuffixPattern = /_BTG_PHASE_\d+$/;
      } else if (promoType === 'pp') {
        getByIdFn = this.promotionCodeDataService.getById3.bind(this.promotionCodeDataService);
        codeSuffixPattern = /_PP_PHASE_\d+$/;
      } else if (promoType === 'free-spin') {
        getByIdFn = this.promotionCodeDataService.getById4.bind(this.promotionCodeDataService);
        codeSuffixPattern = null; // free spin codes don't have a suffix to clean up
      }

      if (getByIdFn) {
        getByIdFn(promoId).subscribe({
          next: (res) => {
            this.data = res;

            if (this.data && ((promoType == 'btg' && this.data.promotion_type == 'BTG Promotion') || (promoType == 'pp' && this.data.promotion_type == 'PP Promotion') || (promoType == 'free-spin' && this.data.promotion_type == 'Free Spin'))) {
              // Clean up code suffix (free spin codes have no suffix, so skip when there's no pattern)
              if (codeSuffixPattern) this.data.code = this.data.code.replace(codeSuffixPattern, '');

              // Normalize 0 → null
              if (this.data.message_template_id === 0) this.data.message_template_id = null;
              if (this.data.message_template_sms_id === 0) this.data.message_template_sms_id = null;

              // Insert KYC and merge data
              this.insertKYCValue();
              Object.assign(this.formData, this.data);

              // Mark all steps as valid
              this.steps.forEach(step => (this.stepValidity[step.label] = true));
            }

            this.onUpdateStep();
            this.loading = false;
          },
          error: () => {
            // Edit-load failed (404/500): the global ApiErrorInterceptor shows the error dialog;
            // this callback just stops the spinner so the wizard doesn't hang forever.
            this.loading = false;
          },
        });
      } else {
        // Invalid or missing promoType
        this.loading = false;
      }
    });

    this.onUpdateStep();
  }

  ngOnDestroy(){
    this.unsubscribeSettingData();
  }

  // Unsubscribe every event-emitter subscription. Called on destroy, and before each
  // updateSubscription() re-wire so handlers don't accumulate across step navigation.
  private unsubscribeSettingData(){
    this.basicSettingSubscription?.unsubscribe();
    this.eligibilitySettingSubscription?.unsubscribe();
    this.memberSettingSubscription?.unsubscribe();
    this.kycSettingSubscription?.unsubscribe();
    this.messageSettingSubscription?.unsubscribe();
    this.promotionNameSettingSubscription?.unsubscribe();
    this.promotionCurrencySettingSubscription?.unsubscribe();
    this.providerBlacklistSubscription?.unsubscribe();
  }

  onUpdateStep(){
    this.updateInjectors();
    this.updateSubscription();
    this.updateStepValidity('Promotion Type', this.isPromotionTypeStepValid());
    this.refreshStepDots();
  }

  onPromotionTypeSelected(type: string){
    this.selectedPromotionType = type;
    this.formData.promotion_type = type;

    this.updateSteps(type);
    this.updateInjectors();
    this.updateStepValidity('Promotion Type', this.isPromotionTypeStepValid());
    this.refreshStepDots();
  }

  // The Promotion Type step is complete only when the chosen type matches the current option:
  // Free Spin needs 'Free Spin'; Provider Promotion needs a real sub-type (BTG/PP). This stops a
  // stale 'Free Spin' (left over from switching tabs) from pre-marking the step valid. Edit mode
  // keeps the loaded type as-is.
  private isPromotionTypeStepValid(): boolean {
    if (this.isEditMode) {
      return !!this.selectedPromotionType;
    }
    if (this.promotionOption === 'free-spin') {
      return this.selectedPromotionType === 'Free Spin';
    }
    return this.selectedPromotionType === 'BTG Promotion' || this.selectedPromotionType === 'PP Promotion';
  }

  private updateSteps(type: string){
    // Free Spin has no Promotion Type step — the type is chosen via the top toggle.
    this.steps = type === 'Free Spin' ? [] : [{ label: 'Promotion Type', component: PromotionTypeComponent }];

    if (type === 'BTG Promotion') {
      this.steps.push(
        { label: 'Basic Setting', component: BasicSettingComponent },
        { label: 'Eligibility Setting', component: EligibilitySettingComponent },
        { label: 'KYC Setting', component: KycSettingComponent },
        { label: 'Message Setting', component: MessageSettingComponent },
        { label: 'Promotion Name', component: PromotionNameSettingComponent },
        { label: 'Promotion Currency', component: PromotionCurrencySettingComponent },
      );
    } else if (type === 'PP Promotion') {
      this.steps.push(
        { label: 'Basic Setting', component: BasicSettingComponent },
        { label: 'Eligibility Setting', component: EligibilitySettingComponent },
        { label: 'Member Setting', component: MemberSettingComponent },
        { label: 'KYC Setting', component: KycSettingComponent },
        { label: 'Message Setting', component: MessageSettingComponent },
        { label: 'Promotion Name', component: PromotionNameSettingComponent },
        { label: 'Promotion Currency', component: PromotionCurrencySettingComponent },
      );
    } else if (type === 'Free Spin') {
      this.steps.push(
        { label: 'Basic Setting', component: BasicSettingComponent },
        { label: 'Eligibility Setting', component: EligibilitySettingComponent },
        { label: 'Member Setting', component: MemberSettingComponent },
        { label: 'KYC Setting', component: KycSettingComponent },
        { label: 'Message Setting', component: MessageSettingComponent },
        { label: 'Promotion Name', component: PromotionNameSettingComponent },
        { label: 'Promotion Currency', component: PromotionCurrencySettingComponent },
        { label: 'Provider Blacklist', component: ProviderBlacklistSettingComponent },
      );
    }

    this.stepValidity = { 'Promotion Type': this.isPromotionTypeStepValid() };
  }

  private updateInjectors(){
    this.stepInjectors = this.steps.map((step) => {
      if (step.label === 'Promotion Type') {
        return Injector.create({
          providers: [
            { provide: 'selectedType', useValue: this.selectedPromotionType },
            { provide: 'onTypeSelected', useValue: (type: string) => this.onPromotionTypeSelected(type) },
            { provide: 'isEditMode', useValue: this.isEditMode },
            { provide: 'promotionOption', useValue: this.promotionOption },
            { provide: 'freeSpinGameProviderId', useValue: this.formData.free_spin_game_provider_id || null }
          ],
          parent: this.injector
        });
      }
      // const initialData = this.stepFormData[step.label] != null ? this.stepFormData[step.label] : (this.data ?? this.formData);
      
      if (this.stepFormData[step.label] != null) {
        this.stepFormData[step.label] = {
          ...this.stepFormData[step.label],
          promotion_type: this.selectedPromotionType
        }
      }
      else {
        this.formData.promotion_type = this.selectedPromotionType;
      }

      // const initialData = this.stepFormData[step.label] != null ?  this.stepFormData[step.label] : this.formData;
      const initialData = this.formData;
      return Injector.create({
        providers: [
          { provide: 'initialData', useValue: initialData },
          { provide: 'isEditMode', useValue: this.isEditMode }
        ],
        parent: this.injector
      });
    });
  }

  async onNext(): Promise<void> {
    this.eventEmit();
    // const currentLabel = this.steps[this.currentStepIndex]?.label;
    // if (currentLabel && !this.ensureStepValid(currentLabel)) {
    //   this.refreshStepDots();
    // }

    if (this.currentStepIndex < this.steps.length - 1) {
      this.currentStepIndex++;
    }

    this.refreshStepDots();
  }

  onBack(){
    this.eventEmit();
    if (this.currentStepIndex > 0) {
      this.currentStepIndex--;
      this.refreshStepDots();
    }
  }

  // Triggers the Provider Blacklist step to save its current selection as a reusable preset (template)
  onSaveBlacklistPreset(){
    this.eventEmitterService.onSaveBlacklistPreset();
  }

  onToTab(i: number){
    this.eventEmit();
    this.currentStepIndex = i;
    this.refreshStepDots();
  }

  eventEmit(){
    this.eventEmitterService.onBasicSettingSave();
    this.eventEmitterService.onEligibilitySettingSave();
    this.eventEmitterService.onMemberSettingSave();
    this.eventEmitterService.onKYCSettingSave();
    this.eventEmitterService.onMessageSettingSave();
    this.eventEmitterService.onPromotionNameSettingSave();
    this.eventEmitterService.onPromotionCurrencySettingSave();
  }

  updateSubscription(){
    // onUpdateStep() calls this on every step change / type change — clear the previous
    // subscriptions first so handlers don't accumulate and fire multiple times per event.
    this.unsubscribeSettingData();
    this.basicSettingSubscription = this.eventEmitterService.basicSettingData.subscribe((formValues) => {
      if (!formValues) return;
      this.stepFormData['Basic Setting'] = formValues;
      this.trackStepChange('Basic Setting', formValues);
      Object.assign(this.formData, formValues);
      this.updateStepValidity('Basic Setting', this.isBasicSettingValid());
      this.refreshStepDots();
    });

    this.eligibilitySettingSubscription = this.eventEmitterService.eligibilitySettingData.subscribe((formValues) => {
      if (!formValues) return;
      this.stepFormData['Eligibility Setting'] = formValues;
      this.trackStepChange('Eligibility Setting', formValues);
      Object.assign(this.formData, formValues);
      this.updateStepValidity('Eligibility Setting', this.isEligibilitySettingValid());
      this.refreshStepDots();
    });

    this.memberSettingSubscription = this.eventEmitterService.memberSettingData.subscribe((formValues) => {
      if (!formValues) return;
      this.stepFormData['Member Setting'] = formValues;
      this.trackStepChange('Member Setting', formValues);
      Object.assign(this.formData, formValues);
      this.updateStepValidity('Member Setting', this.isMemberSettingValid());
      this.refreshStepDots();
    });

    this.kycSettingSubscription = this.eventEmitterService.kycSettingData.subscribe((formValues) => {
      if (!formValues) return;
      this.stepFormData['KYC Setting'] = formValues;
      this.trackStepChange('KYC Setting', formValues);
      Object.assign(this.formData, formValues);
      this.updateStepValidity('KYC Setting', this.isKYCSettingValid());
      this.refreshStepDots();
    });

    this.messageSettingSubscription = this.eventEmitterService.messageSettingData.subscribe((formValues) => {
      if (!formValues) return;
      this.stepFormData['Message Setting'] = formValues;
      this.trackStepChange('Message Setting', formValues);
      Object.assign(this.formData, formValues);
      this.updateStepValidity('Message Setting', this.isMessageSettingValid());
      this.refreshStepDots();
    });

    this.promotionNameSettingSubscription = this.eventEmitterService.promotionNameSettingData.subscribe((formValues) => {
      if (!formValues) return;
      this.stepFormData['Promotion Name'] = { promotion_names: formValues };
      this.trackStepChange('Promotion Name', formValues);
      this.formData.promotion_names = formValues;

      this.updateStepValidity('Promotion Name', this.isPromotionNameSettingValid());
      this.refreshStepDots();
    });

    this.promotionCurrencySettingSubscription = this.eventEmitterService.promotionCurrencySettingData.subscribe((formValues) => {
      if (!formValues) return;
      this.stepFormData['Promotion Currency'] = { promotion_currency: formValues };
      this.trackStepChange('Promotion Currency', formValues);
      this.formData.promotion_currency = formValues;

      this.updateStepValidity('Promotion Currency', this.isPromotionCurrencySettingValid());
      this.refreshStepDots();
    });

    this.providerBlacklistSubscription = this.eventEmitterService.providerBlacklistData.subscribe((data: any) => {
      if (!data) return;
      this.stepFormData['Provider Blacklist'] = data;
      this.trackStepChange('Provider Blacklist', data);
      this.formData.black_list_sub_categories = data.black_list_sub_categories || [];
      this.updateStepValidity('Provider Blacklist', true); // blacklist is optional
      this.refreshStepDots();
    });
  }

  // Edit mode: Update stays disabled until something actually changes.
  //
  // The baseline can't be the raw API payload — every step normalizes its values as it mounts
  // (Number() coercions on toggles, `?? 0` defaults, reformatted dates). Nor can it be the step's
  // first emission: several steps patch their own form once an HTTP call lands (Basic Setting
  // sets promotion_ids after the promotion list loads, Eligibility sets game_provider_ids after
  // the provider list), which would read as an edit nobody made.
  //
  // So a step's baseline keeps re-capturing until the operator actually touches that step —
  // onStepInteraction() marks it from a DOM event inside the step content. From then on the
  // comparison is live, so reverting a change by hand disables the button again. Tracked per
  // step, so touching one step doesn't freeze another step's baseline while it is still loading.
  private stepBaseline: { [label: string]: string } = {};
  private stepSnapshot: { [label: string]: string } = {};
  private interactedSteps = new Set<string>();
  hasUnsavedChanges = false;

  onStepInteraction() {
    if (!this.isEditMode) return;
    const label = this.steps[this.currentStepIndex]?.label;
    if (label) this.interactedSteps.add(label);
  }

  private trackStepChange(label: string, values: any) {
    if (!this.isEditMode) return; // create mode gates on step validity instead
    const snapshot = JSON.stringify(values ?? null);
    if (!this.interactedSteps.has(label)) {
      this.stepBaseline[label] = snapshot; // still settling — not an operator edit
    }
    this.stepSnapshot[label] = snapshot;
    this.hasUnsavedChanges = Object.keys(this.stepSnapshot)
      .some(key => this.stepSnapshot[key] !== this.stepBaseline[key]);
  }

  private ensureStepValid(label: string): boolean {
    if (label === 'Promotion Type') {
      const ok = this.isPromotionTypeStepValid();
      this.updateStepValidity(label, ok);
      return ok;
    }

    if (label === 'Basic Setting') {
      const ok = this.isBasicSettingValid();
      this.updateStepValidity(label, ok);
      return ok;
    }

    if (label === 'Eligibility Setting') {
      const ok = this.isEligibilitySettingValid();
      this.updateStepValidity(label, ok);
      return ok;
    }

    if (label === 'Member Setting') {
      const ok = this.isMemberSettingValid();
      this.updateStepValidity(label, ok);
      return ok;
    }

    if (label === 'KYC Setting') {
      const ok = this.isKYCSettingValid();
      this.updateStepValidity(label, ok);
      return ok;
    }

    if (label === 'Message Setting') {
      const ok = this.isMessageSettingValid();
      this.updateStepValidity(label, ok);
      return ok;
    }

    this.updateStepValidity(label, null);
    return true;
  }

  private isBasicSettingValid(): boolean {
    const c = this.stepFormData['Promotion Type'] ?? this.formData;
    const d = this.stepFormData['Basic Setting'] ?? this.formData;
    const hasCode = !!d?.code && String(d.code).trim().length > 0;
    const hasName = !!d?.name && String(d.name).trim().length > 0;
    const hasValidFrom = !!d?.valid_from && String(d.valid_from).trim().length > 0;

    if (c?.promotion_type == 'PP Promotion') {
      const hasFreeSpinGameCode = !!d?.free_spin_game_code && String(d.free_spin_game_code).trim().length > 0;
      return hasCode && hasName && hasValidFrom && hasFreeSpinGameCode;
    }
    else if (c?.promotion_type == 'Free Spin') {
      const hasProvider = !!d?.free_spin_game_provider_id;
      const hasGame = !!d?.free_spin_game_code && String(d.free_spin_game_code).trim().length > 0;
      const hasSubType = !!d?.promo_sub_type;
      // No frequency gate: the picker was removed from Basic Setting, so an operator has no way to
      // satisfy it. Create always submits frequency_type 1 (where the API makes `frequency`
      // optional) and edit passes the stored value straight through — PromotionV4UpdateRequest
      // only requires `frequency` to be an array, so a legacy 2/3 with no days still saves.
      return hasCode && hasName && hasValidFrom && hasProvider && hasGame && hasSubType;
    }
    else {
      return hasCode && hasName && hasValidFrom;
    }
  }

  private isEligibilitySettingValid(): boolean {
    const d = this.stepFormData['Eligibility Setting'] ?? this.formData;
    const hasDaily_max = !!d?.daily_max && String(d.daily_max).trim().length > 0;

    if (this.selectedPromotionType == 'Free Spin') {
      // Bypass Turnover = immediate unlock: the Target Amount inputs are hidden and auto-defaulted,
      // and the game provider is enforced by Basic Setting — so only Daily Max is required here.
      if (+d?.bypass_turnover === 1) {
        return hasDaily_max;
      }
      const has = (v: any) => v !== null && v !== undefined && String(v).trim().length > 0;
      const hasTargetType = has(d?.target_type);
      const hasTransferAmount = has(d?.type); // Transfer Amount (Included/Excluded)
      const hasTargetMultiplier = has(d?.target_multiplier);
      const hasGameProvider = Array.isArray(d?.target) && d.target.length > 0
        && Array.isArray(d.target[0]?.game_provider_ids) && d.target[0].game_provider_ids.length > 0;
      return hasDaily_max && hasTargetType && hasTransferAmount && hasTargetMultiplier && hasGameProvider;
    }

    return hasDaily_max;
  }

  private isMemberSettingValid(): boolean {
    return true;
  }

  private isKYCSettingValid(): boolean {
    const d = this.stepFormData['KYC Setting'] ?? this.formData;

    if (!d) return false;
    const selectedOption = d.selectedOption || 'none';
    
    if (selectedOption === 'manual') {
      const kyc_option = d.kyc_option || null;
      return Array.isArray(kyc_option) ? kyc_option.length > 0 : !!kyc_option;
    } 
    
    if (selectedOption === 'kyc-status') {
      const kyc_status = d.kyc_status || null;
      return Array.isArray(kyc_status) ? kyc_status.length > 0 : !!kyc_status;
    } 
    
    if (selectedOption === 'none') {
      return true;
    }

    return false;
  }

  private isMessageSettingValid(): boolean {
    return true;
  }

  private isPromotionNameSettingValid(): boolean {
    return true;
  }

  private isPromotionCurrencySettingValid(): boolean {
    const d = this.stepFormData['Promotion Currency']?.promotion_currency ?? this.formData?.promotion_currency;

    if (!Array.isArray(d) || d.length === 0) return false;

    if (this.selectedPromotionType == 'Free Spin') {
      const has = (v: any) => v !== null && v !== undefined && String(v).trim().length > 0;
      // Only currencies with Status ON are submitted, so only they need valid free-round params.
      // At least one currency must be ON, otherwise there is nothing to submit.
      const enabled = d.filter((c: any) => +c?.status === 1);
      if (enabled.length === 0) return false;
      return enabled.every(c =>
        has(c.max_total_applications) && has(c.max_total_bonus) && has(c.max_transfer_out)
        && has(c.amount_per_line) && has(c.rounds) && has(c.lines) && has(c.coins)
      );
    }

    return d.every(currency => {
      const hasMaxTotalApplication = currency.max_total_applications !== null && currency.max_total_applications !== '';
      const hasMaxTotalBonus = currency.max_total_bonus !== null && currency.max_total_bonus !== '';
      const hasPhase2TargetType = !!currency?.phase2_target_type;
      const hasMaxTransferOut =
        currency?.max_transfer_out !== null &&
        currency?.max_transfer_out !== undefined &&
        currency?.max_transfer_out !== '';
      const hasPhase2TargetAmount =
        currency?.phase2_target_amount !== null &&
        currency?.phase2_target_amount !== undefined &&
        currency?.phase2_target_amount !== '';

      return hasMaxTotalApplication && hasMaxTotalBonus && hasPhase2TargetType && hasMaxTransferOut
        && hasPhase2TargetAmount && this.isPhase2StartingAmountValid(currency);
    });
  }

  // PP Promotion: Phase 2 Starting Amount is the credit the member starts Phase 2 with, so 0 (or a
  // blank the field resets to 0) would create a promotion that grants nothing. The API only validates
  // it as `numeric`, so this is the only gate. BTG keeps its fixed, read-only value untouched.
  private isPhase2StartingAmountValid(currency: any): boolean {
    if (this.selectedPromotionType !== 'PP Promotion') return true;
    return +currency?.phase2_starting_amount > 0;
  }

  private updateStepValidity(label: string, isValid: boolean | null){
    this.stepValidity[label] = isValid;
  }

  private refreshStepDots(){
    this.steps.forEach(step => {
      const v = this.stepValidity[step.label];

      if (v === true) {
        this.stepDotClasses[step.label] = 'passed-dot';
      } else if (v === false) {
        this.stepDotClasses[step.label] = 'red-dot';
      } else {
        this.stepDotClasses[step.label] = 'default-dot';
      }
    });
  }

  getStepDotClass(step: string): string {
    return this.stepDotClasses[step] || 'default-dot';
  }

  // For Free Spin the 'Member Setting' step is shown as 'Eligible Type Setting' (internal label unchanged).
  displayStepLabel(label: string): string {
    if (label === 'Member Setting' && this.selectedPromotionType === 'Free Spin') {
      return 'Eligible Type Setting';
    }
    return label;
  }

  insertKYCValue() {
    let kycOption: number[] = [];
    let kycStatus: number[] = [];

    if (this.data.kyc_basic === 1) {kycStatus.push(1);}
    if (this.data.kyc_advanced === 1) {kycStatus.push(2);}
    if (this.data.kyc_pro === 1) {kycStatus.push(3);}
    if (this.data.requires_email === 1) {kycOption.push(1);}
    if (this.data.requires_mobile === 1) {kycOption.push(2);}
    if (this.data.requires_dob === 1) {kycOption.push(3);}
    if (this.data.requires_fullname === 1) {kycOption.push(4);}

    this.data['kyc_option'] = kycOption;
    this.data['kyc_status'] = kycStatus;
  }

  async onSubmit(): Promise<void> {
    if (this.isEditMode && !this.canEditPromotionCode) {
      return;
    }
    if (this.submitting) return; // guard against double-submit
    this.submitting = true;
    this.eventEmitterService.onBasicSettingSave();
    this.eventEmitterService.onEligibilitySettingSave();
    this.eventEmitterService.onMemberSettingSave();
    this.eventEmitterService.onKYCSettingSave();
    this.eventEmitterService.onMessageSettingSave();
    this.eventEmitterService.onPromotionNameSettingSave();
    this.eventEmitterService.onPromotionCurrencySettingSave();

    let paramFormData = {
      ...this.formData,
      limit_transfer_in: true,
      limit_transfer_out: true,
      bonus_rate: 0,
      validity: 1,
      reward_validity: 1,
      frequency: [],
      recurring: 1,
      frequency_type: 1,
      eligible_types: 1,
      allow_cancel: 0,
      reset_frequency: 1,
      visible_by_affiliate: false,
      valid_from: this.isValidDate(this.formData.valid_from) ? moment(this.formData.valid_from).tz(this.timezone, true).utc().format('YYYY-MM-DD HH:mm:ss') : null,
      valid_to: this.isValidDate(this.formData.valid_to) ? moment(this.formData.valid_to).tz(this.timezone, true).utc().format('YYYY-MM-DD HH:mm:ss') : null,
      kyc_basic: this.formData.kyc_status != null ? (this.formData.kyc_status.includes(1) ? 1 : 0) : 0,
      kyc_advanced: this.formData.kyc_status != null ? (this.formData.kyc_status.includes(2) ? 1 : 0) : 0,
      kyc_pro: this.formData.kyc_status != null ? (this.formData.kyc_status.includes(3) ? 1 : 0) : 0,
      requires_email: this.formData.kyc_option != null ? (this.formData.kyc_option.includes(1) ? 1 : 0) : 0,
      requires_mobile: this.formData.kyc_option != null ? (this.formData.kyc_option.includes(2) ? 1 : 0) : 0,
      requires_dob: this.formData.kyc_option != null ? (this.formData.kyc_option.includes(3) ? 1 : 0) : 0,
      requires_fullname: this.formData.kyc_option != null ? (this.formData.kyc_option.includes(4) ? 1 : 0) : 0,
      transfer_unlock: this.formData?.transfer_unlock || 0,
      auto_unlock: this.formData?.auto_unlock || 1,
      last_deposit: this.formData?.last_deposit || 0,
      auto_approve: this.formData?.auto_approve || 1,
      max_per_player: this.formData?.max_per_player || 0,
      daily_max:  this.formData?.daily_max || 1,
    }
    delete paramFormData['current_players'];
    delete paramFormData['used_budget'];
    
    if (this.formData.promotion_type == 'BTG Promotion') {
      delete paramFormData['free_spin_game_code'];
      delete paramFormData['amount_per_line'];
      delete paramFormData['rounds'];
    }

    // Free Spin uses the real Recurring/Frequency values from Basic Setting instead of the
    // hardcoded defaults above. PP / BTG keep the hardcoded defaults (behavior unchanged).
    if (this.formData.promotion_type == 'Free Spin') {
      paramFormData.recurring = this.formData.recurring ?? 0;
      paramFormData.frequency_type = this.formData.frequency_type ?? 1;
      paramFormData.reset_frequency = this.formData.reset_frequency ?? 0;
      paramFormData.frequency = this.formData.frequency ?? [];
      paramFormData.reward_validity = this.formData.reward_validity ?? 0;
      // Validity is operator-editable for Free Spin (Basic Setting). Fall back to the previous
      // hardcoded 1 when the input is left blank — the API rejects a null validity.
      paramFormData.validity = +this.formData.validity > 0 ? +this.formData.validity : 1;
      paramFormData.reset_day = this.formData.reset_day ?? 0;
      paramFormData.eligible_types = this.formData.eligible_types ?? 1;
      paramFormData.visible_by_affiliate = this.formData.visible_by_affiliate ? true : false;
      // Auto Unlock / Unlock Upon Transfer Out are mutually exclusive and can legitimately be 0.
      // Use ?? (not the generic `|| 1` / `|| 0` defaults above) so an operator-selected auto_unlock=0
      // isn't forced back to 1 — otherwise the transfer-out unlock gate can never take effect.
      paramFormData.auto_unlock = this.formData.auto_unlock ?? 1;
      paramFormData.transfer_unlock = this.formData.transfer_unlock ?? 0;
      // Always derive the target-category flags from target_type so an edit that never re-opened the
      // Eligibility step (where the form sets these) doesn't send showV4's category_id arrays back —
      // those fail the API's `boolean` validation ("must be true or false").
      paramFormData.target_promotion_category_turnover = +this.formData.target_type === 6 ? 1 : 0;
      paramFormData.target_promotion_category_winloss = +this.formData.target_type === 7 ? 1 : 0;
      // Only submit currencies whose Status is ON — excluded currencies are skipped entirely
      // (not saved, and never sent to the provider free-round grant).
      paramFormData.promotion_currency = (this.formData.promotion_currency || []).filter((c: any) => +c?.status === 1);
    }

    if (this.isEditMode) {
      paramFormData.id = this.data.id;
      switch (paramFormData.promotion_type) {
        case 'BTG Promotion':
          setTimeout(() => {
            this.subscription = forkJoin([
              this.promotionCodeDataService.updatev2(paramFormData).pipe(
                tap((res: any) => {
                  this.submitting = false;
                  this.setListingAutoSearch();
                }),
                catchError((error) => {
                  this.submitting = false;
                  throw error;
                })
              ),
              this.promotionCodeDataService.messages$
            ]).subscribe();
          }, 0);
          break;
        case 'PP Promotion':
          setTimeout(() => {
            this.subscription = forkJoin([
              this.promotionCodeDataService.updatev3(paramFormData).pipe(
                tap((res: any) => {
                  this.submitting = false;
                  this.setListingAutoSearch();
                }),
                catchError((error) => {
                  this.submitting = false;
                  throw error;
                })
              ),
              this.promotionCodeDataService.messages$
            ]).subscribe();
          }, 0);
          break;
        case 'Free Spin':
          setTimeout(() => {
            this.subscription = forkJoin([
              this.promotionCodeDataService.updatev4(paramFormData).pipe(
                tap((res: any) => {
                  this.submitting = false;
                  this.setListingAutoSearch();
                }),
                catchError((error) => {
                  this.submitting = false;
                  throw error;
                })
              ),
              this.promotionCodeDataService.messages$
            ]).subscribe();
          }, 0);
          break;
      }

    } else {
      switch (paramFormData.promotion_type) {
        case 'BTG Promotion':
          setTimeout(() => {
            this.subscription = forkJoin([
              this.promotionCodeDataService.addv2(paramFormData).pipe(
                tap((res: any) => {
                  this.submitting = false;
                  this.setListingAutoSearch();
                }),
                catchError((error) => {
                  this.submitting = false;
                  throw error;
                })
              ),
              this.promotionCodeDataService.messages$
            ]).subscribe();
          }, 0);
          break;
        case 'PP Promotion':
          setTimeout(() => {
            this.subscription = forkJoin([
              this.promotionCodeDataService.addv3(paramFormData).pipe(
                tap((res: any) => {
                  this.submitting = false;
                  this.setListingAutoSearch();
                }),
                catchError((error) => {
                  this.submitting = false;
                  throw error;
                })
              ),
              this.promotionCodeDataService.messages$
            ]).subscribe();
          }, 0);
          break;
        case 'Free Spin':
          setTimeout(() => {
            this.subscription = forkJoin([
              this.promotionCodeDataService.addv4(paramFormData).pipe(
                tap((res: any) => {
                  this.submitting = false;
                  this.setListingAutoSearch();
                }),
                catchError((error) => {
                  this.submitting = false;
                  throw error;
                })
              ),
              this.promotionCodeDataService.messages$
            ]).subscribe();
          }, 0);
          break;
      }
    }

    localStorage.removeItem('promotionCodeDropdownList');
  }

  // The listing (3.2.1) reads this key on load and clears it — see checkAutoLoad() in
  // promotion-code.component.ts. 'all' runs its default search (any other value would filter by
  // that promotion id), so the OK button lands the operator on the full list rather than an
  // empty, unsearched grid.
  private setListingAutoSearch() {
    localStorage.setItem('promotion_auto_search', 'all');
  }

  isValidDate(date: Date | string) {
    if (moment(date).isValid()) {
      return true;
    }
    return false;
  }

  private onResetForm() {
    this.data = null;
  }

  onCreateNew() {
    this.onResetForm();
    this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
      this.router.navigateByUrl('general/promotion-setup');
    });
  }

  onReload(e: Event) {
    this.router.navigate(['general/promotion-codes']);
  }

  // A successful save / update sends the operator to the promotion codes list (onReload), which only
  // searches on load when this marker is set — otherwise they arrive at an empty table. The value is
  // the saved promotion's id when known (edit, or a create whose response returns the row), else
  // 'all' to fall back to the list's default search. Deliberately not the CRM pages'
  // `promotion_auto_search_by_id` key — those consume it too and would hijack their own search.
  private flagPromotionListSearch(promotionId?: any) {
    localStorage.setItem('promotion_auto_search', promotionId ? String(promotionId) : 'all');
  }

  findInvalidControls() {
    const controls = this.formData;
    for (const name in controls) {
        if (this.formDataValidated.includes(name) && !controls[name]) {
            return true;
        }
    }
    // Free Spin has extra API-required fields (free-round provider/game + claim frequency in Basic Setting,
    // target amount in Eligibility, per-currency free-round params in Promotion Currency). Reuse the same
    // checks that drive the step red-dots so the Submit button and the step indicators agree — this stops
    // the wizard submitting an incomplete Free Spin payload that the API would only reject with a 422.
    // Scoped to Free Spin: PP / BTG submit behaviour is unchanged.
    if (this.selectedPromotionType === 'Free Spin') {
        if (!this.isBasicSettingValid() || !this.isEligibilitySettingValid() || !this.isPromotionCurrencySettingValid()) {
            return true;
        }
    }
    // PP Promotion: block submit on a zero / blank Phase 2 Starting Amount. Scoped to that one field so
    // the rest of PP's submit behaviour is unchanged — the other currency fields stay as they were.
    if (this.selectedPromotionType === 'PP Promotion') {
        const currencies = this.stepFormData['Promotion Currency']?.promotion_currency ?? this.formData?.promotion_currency;
        if (Array.isArray(currencies) && currencies.some((c: any) => !this.isPhase2StartingAmountValid(c))) {
            return true;
        }
    }
    return false;
  }

  onSelectPromotionOption(option: string) {
    this.promotionOption = option;
    this.currentStepIndex = 0;
    // Free Spin is a single type with no Promotion Type step — select it here so the steps
    // rebuild. Provider Promotion restores its Promotion Type step for sub-type selection.
    if (option === 'free-spin') {
      this.onPromotionTypeSelected('Free Spin');
    } else {
      // Provider Promotion: restore the Promotion Type step so the sub-type can be chosen.
      // Keep selectedPromotionType truthy so the step doesn't auto-default back to Free Spin.
      this.steps = [{ label: 'Promotion Type', component: PromotionTypeComponent }];
    }
    this.onUpdateStep();
  }
}
